import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createCommentVNode as _createCommentVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "q-ma-none" }
const _hoisted_2 = { class: "q-pa-none q-ma-none" }

import type { PropType } from 'vue';
import { ref, toRefs, onBeforeUnmount, watch, reactive } from 'vue';
import { useFocus } from '@vueuse/core';

// Personal Components
import QCustomInput from '@components/form/QCustomInput.vue';
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// Stores
import { useSecurityStore } from '@stores/security';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal libs
import { $t } from 'src/i18n/utils';
import { IConfigSecurity } from '@shared/interface/config.interface';
import {
  displayDialogError,
  displayNotifSuccess,
} from '@shared/lib/notify.lib';
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { prepareAuthPayload } from '@shared/lib/login.lib';
import { SecretTypeEnum } from '@shared/enum/secret-type.enum'
import { InputFiedTypeEnum } from '@shared/enum/input.enum'
import { FormNameEnum } from '@shared/enum/form-name.enum';

/**
 * Properties
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'DialogAuthenticate',
  props: {
  show: { required: true, type: Boolean, default: false },
  securityRules: { required: false, type: Object as PropType<IConfigSecurity> },
},
  emits: ['close', 'show-forgotten-pass', 'show-signup'],
  setup(__props, { emit: __emit }) {

// VENDOR
const props = __props;
const { securityRules, show } = toRefs(props);

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Stores
 */
const securityStore = useSecurityStore()

/**
 * HTMLElement refs
 */
const qInputPassword = ref();
const qInputEmail = ref();
const qBtnNewAccount = ref<HTMLButtonElement>();

const { focused: qBtnNewAccountFocused } = useFocus(qBtnNewAccount);

/**
 * Emits
 */
const emits = __emit;

/**
 * Data
 */
const loading = ref<boolean | undefined>(false);
const email = ref<string | undefined>(undefined);
const password = ref<string | undefined>(undefined);
const chkRemember = ref<boolean>(true);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function onForgottenPassClick() {
  emits('show-forgotten-pass');
  emits('close');
}

function onSignupClick() {
  emits('show-signup');
  emits('close');
}

function onCancelClick() {
  emits('close');
}

function validForm(): boolean {
    // Reset focus
    qInputEmail.value && qInputEmail.value?.blur()
    qInputPassword.value && qInputPassword.value?.blur()

    // Validate
    qInputEmail.value && qInputEmail.value?.validate()
    qInputPassword.value && qInputPassword.value?.validate()

    if (qInputEmail.value != null && qInputEmail.value.hasError()) {
      qInputEmail.value.focus()
      return false;
    }

    if (qInputPassword.value != null && qInputPassword.value.hasError()) {
      qInputPassword.value.focus()
      return false;
    }

    return true;
}

async function onOkClick() {
  if (!validForm()) {
    return;
  }

  loading.value = true;

  const fingerprint = await securityStore.getFingerprint()

  const payload = await prepareAuthPayload({
    email: email.value,
    password: password.value,
    chkRemember: chkRemember.value,
    fingerprint: fingerprint,
    csrf: (await securityStore.getCsrf(FormNameEnum.LOGIN))?.token
  })
  
  // Do login
  if (password.value && email.value) {
    sessionManager
      .auth(payload)
      .then(() => {
        displayNotifSuccess('Connexion réussie');
        emits('close');
      })
      .catch((error: Error) =>
        displayDialogError({
          title: 'Erreur',
          message: error.message ?? 'null',
          isHtml: true,
        }),
      )
      .finally(() => (loading.value = false));
  }
}

async function initChkRemember() {
  const activeUser = await dbGetActiveUser();

  // Set chkRemember checkbox value
  if (activeUser != null) {
    chkRemember.value = activeUser.chkRemember === true;

    // Set email if needed
    if (chkRemember.value) {
      email.value = activeUser.email;
    } else {
      email.value = undefined
    }
  } else {
    chkRemember.value = true;
    email.value = undefined
  }

  // Set focus
  qBtnNewAccountFocused.value = true;
}

/**
 * Watch
 */
watch(
  () => show.value,
  async (newValue: boolean | undefined) => {
    if (newValue) {
      qInputPassword.value?.resetValidation();
      password.value = undefined;

      initChkRemember();
    }
  },
  { immediate: true }
);

/**
 * Hooks
 */
onBeforeUnmount(() => {
  password.value = undefined;
  loading.value = undefined;
});

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_checkbox = _resolveComponent("q-checkbox")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    ref: "qDialogTemplate",
    "show-dialog": _unref(show),
    title: _unref($t)('signin.title'),
    maximized: _unref(isMobile),
    persistent: true,
    "loading-first": loading.value,
    onClose: _cache[7] || (_cache[7] = ($event: any) => (onClose()))
  }, {
    header: _withCtx(() => [
      _createVNode(QBtnValid, {
        ref_key: "qBtnNewAccount",
        ref: qBtnNewAccount,
        label: !_unref(isMobile) ? _unref($t)('signin.signupBtn.label') : undefined,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (onSignupClick())),
        type: 'button',
        disable: loading.value,
        "icon-right": !_unref(isMobile) ? 'person_add' : undefined,
        "icon-left": _unref(isMobile) ? 'person_add' : undefined,
        "is-secondary": true,
        "color-class": 'bg-white text-black'
      }, null, 8 /* PROPS */, ["label", "disable", "icon-right", "icon-left"])
    ]),
    content: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius q-pt-sm"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "q-pt-none q-pb-md" }, {
            default: _withCtx(() => [
              _createElementVNode("p", _hoisted_1, _toDisplayString(_unref($t)('signin.banner.password')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_q_card_section, { class: "text-caption q-my-none q-py-none" }, {
            default: _withCtx(() => [
              _createElementVNode("p", _hoisted_2, _toDisplayString(_unref($t)('generic.form.mandatoryFields')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_q_card_section, { class: "q-pa-none q-pt-none" }, {
            default: _withCtx(() => [
              _createCommentVNode(" Input : email "),
              _createVNode(QCustomInput, {
                modelValue: email.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((email).value = $event)),
                ref_key: "qInputEmail",
                ref: qInputEmail,
                "input-type": _unref(InputFiedTypeEnum).EMAIL,
                disable: loading.value,
                "extra-class": 'q-mx-sm',
                "on-enter-key": onOkClick
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "disable"]),
              _createCommentVNode(" Input : password "),
              _createVNode(QCustomInput, {
                modelValue: password.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                ref_key: "qInputPassword",
                ref: qInputPassword,
                "input-type": _unref(InputFiedTypeEnum).PASSWORD,
                "secret-type": _unref(SecretTypeEnum).NORMAL,
                disable: loading.value,
                "extra-class": 'q-mx-sm',
                "on-enter-key": onOkClick
              }, null, 8 /* PROPS */, ["modelValue", "input-type", "secret-type", "disable"])
            ]),
            _: 1 /* STABLE */
          }),
          _createVNode(_component_q_card_section, { class: "q-pt-none" }, {
            default: _withCtx(() => [
              _createCommentVNode(" CHECKBOX REMEMBER "),
              _createVNode(_component_q_checkbox, {
                class: "set-outline text-caption q-mt-sm",
                modelValue: chkRemember.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((chkRemember).value = $event)),
                disable: loading.value,
                label: _unref($t)('signin.remember')
              }, null, 8 /* PROPS */, ["modelValue", "disable", "label"])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_card, {
        flat: "",
        class: "no-border-radius"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_separator),
          _createVNode(_component_q_card_actions, { class: "row justify-between" }, {
            default: _withCtx(() => [
              _createElementVNode("div", null, [
                _createCommentVNode(" Forgotten password "),
                _createVNode(QBtnCancel, {
                  label: _unref($t)('signin.forgottenPass.label'),
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (onForgottenPassClick())),
                  type: 'button',
                  disable: loading.value,
                  title: _unref($t)('signin.forgottenPass.title.onLine')
                }, null, 8 /* PROPS */, ["label", "disable", "title"])
              ]),
              _createElementVNode("div", null, [
                (!_unref(isMobile))
                  ? (_openBlock(), _createBlock(QBtnCancel, {
                      key: 0,
                      class: "q-mr-xs",
                      label: _unref($t)('signin.signinCancel.label'),
                      type: 'button',
                      disable: loading.value,
                      "icon-left": 'close',
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (onCancelClick()))
                    }, null, 8 /* PROPS */, ["label", "disable"]))
                  : _createCommentVNode("v-if", true),
                _createVNode(QBtnValid, {
                  label: _unref($t)('signin.signinOk.label'),
                  type: 'button',
                  loading: loading.value,
                  disable: loading.value,
                  "loading-label": _unref($t)('signin.loader.label'),
                  "icon-right": 'login',
                  onClick: _cache[6] || (_cache[6] = ($event: any) => (onOkClick()))
                }, null, 8 /* PROPS */, ["label", "loading", "disable", "loading-label"])
              ])
            ]),
            _: 1 /* STABLE */
          })
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "title", "maximized", "loading-first"]))
}
}

})